import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import { company } from '../mock';
import { useContent } from '../content';
import { useLang } from '../i18n';

const legalRoutes = {
  'Privacy Policy': '/privacy-policy', 'Datenschutzerklärung': '/privacy-policy',
  'Terms & Conditions': '/terms-and-conditions', 'Allgemeine Geschäftsbedingungen': '/terms-and-conditions',
  'Impressum': '/impressum'
};

const Footer = ({ onTabChange }) => {
  const { footerData, services } = useContent();
  const { t } = useLang();
  const serviceSlug = (title) => {
    const match = services.items.find((s) => s.title === title);
    return match ? `/services/${match.slug}` : '/';
  };
  return (
    <footer data-testid="site-footer" className="bg-[#F3F4F6] dark:bg-navy-deep text-navy dark:text-white">
      <div className="max-w-[1680px] mx-auto px-6 md:px-10 py-20">
        <div className="grid lg:grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))] gap-12 lg:gap-10">
          {/* Brand */}
          <div>
            <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="inline-block" title="Intervantis Transport Solutions" aria-label="Intervantis home">
              <img src="/brand/intervantis-transport-horizontal.png" alt="Intervantis Transport Solutions" className="h-14 w-auto object-contain dark:brightness-0 dark:invert" draggable="false" />
            </Link>
            <p className="mt-6 text-navy/60 dark:text-white/60 font-body text-sm leading-relaxed max-w-sm">{footerData.blurb}</p>
            <div className="mt-7 flex items-center gap-3">
              <a
                href={company.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="Intervantis on LinkedIn"
                className="group inline-flex items-center gap-2.5 border border-navy/20 dark:border-white/20 px-4 py-2.5 font-body text-sm hover:border-navy dark:hover:border-white transition-colors"
              >
                <Linkedin size={16} strokeWidth={1.8} />
                LinkedIn
                <ArrowUpRight size={14} className="text-navy/50 dark:text-white/50 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href={company.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Intervantis on Instagram"
                className="group inline-flex items-center gap-2.5 border border-navy/20 dark:border-white/20 px-4 py-2.5 font-body text-sm hover:border-navy dark:hover:border-white transition-colors"
              >
                <Instagram size={16} strokeWidth={1.8} />
                Instagram
                <ArrowUpRight size={14} className="text-navy/50 dark:text-white/50 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <div className="label-mono text-navy/45 dark:text-white/45 mb-5">{t('footer.services')}</div>
            <ul className="space-y-3">
              {footerData.services.map((s) => (
                <li key={s}>
                  <Link to={serviceSlug(s)} className="text-left text-navy/70 dark:text-white/70 hover:text-ember font-body text-sm leading-snug transition-colors">{s}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <div className="label-mono text-navy/45 dark:text-white/45 mb-5">{t('footer.industries')}</div>
            <ul className="space-y-3">
              {footerData.industries.map((s) => (
                <li key={s}>
                  <button onClick={() => onTabChange('expertise')} className="text-left text-navy/70 dark:text-white/70 hover:text-ember font-body text-sm leading-snug transition-colors">{s}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="label-mono text-navy/45 dark:text-white/45 mb-5">{t('footer.contact')}</div>
            <div className="text-navy/70 dark:text-white/70 font-body text-sm leading-relaxed">
              {company.address.map((a) => (<div key={a}>{a}</div>))}
            </div>
            <a href={`tel:${company.phone.replace(/\s/g, '')}`} className="mt-4 block text-navy/70 dark:text-white/70 hover:text-ember font-body text-sm transition-colors">{company.phone}</a>
            {company.emails.map((em) => (
              <a key={em} href={`mailto:${em}`} className="block text-navy/70 dark:text-white/70 hover:text-ember font-body text-sm transition-colors">{em}</a>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-6 border-t border-navy/10 dark:border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span className="label-mono text-navy/45 dark:text-white/45">{footerData.copyright}</span>
          <div className="flex flex-wrap gap-6">
            {footerData.legal.map((l) => (
              <a
                key={l}
                href={legalRoutes[l] || '/'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-navy/55 dark:text-white/55 hover:text-ember font-body text-sm transition-colors"
              >
                {l}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
